import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import MKTypography from "components/MKTypography";
import SafeImage from "components/SafeImage";

// Both public galleries used the .photo-container / .photo rules in App.css,
// which pinned every image to a third of the row at any screen size and had
// no loading, empty or error state at all.
function PhotoGallery({ fetchImages, emptyText, alt }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState("");

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const result = await fetchImages();
        // `res.data` used to be read straight off a possibly-undefined result,
        // which threw before anything rendered when the document was missing.
        if (active) setImages(Array.isArray(result?.data) ? result.data : []);
      } catch (loadError) {
        console.error("Could not load images:", loadError);
        if (active) setError("We could not load these photos. Please refresh the page.");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [fetchImages]);

  return (
    <Container sx={{ py: { xs: 3, md: 4 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Grid container spacing={2.5}>
          {[0, 1, 2, 3, 4, 5].map((key) => (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <Skeleton variant="rounded" sx={{ aspectRatio: "3 / 4", height: "auto" }} />
            </Grid>
          ))}
        </Grid>
      ) : images.length === 0 ? (
        <MKTypography variant="body2" color="text" sx={{ py: 8, textAlign: "center" }}>
          {emptyText}
        </MKTypography>
      ) : (
        <Grid container spacing={2.5}>
          {images.map((image) => (
            <Grid item xs={12} sm={6} md={4} key={image}>
              <Card
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: "none",
                  border: ({ palette: { brand } }) => `1px solid ${brand.line}`,
                  transition: "transform 200ms ease, box-shadow 200ms ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: ({ boxShadows: { lg } }) => lg,
                  },
                }}
              >
                <SafeImage src={image} alt={alt} onClick={() => setLightbox(image)} />
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={Boolean(lightbox)} onClose={() => setLightbox("")} maxWidth="lg">
        <Box
          component="img"
          src={lightbox}
          alt={alt}
          sx={{ maxWidth: "100%", maxHeight: "88vh", display: "block" }}
        />
      </Dialog>
    </Container>
  );
}

PhotoGallery.defaultProps = { emptyText: "Nothing has been published here yet." };

PhotoGallery.propTypes = {
  fetchImages: PropTypes.func.isRequired,
  emptyText: PropTypes.string,
  alt: PropTypes.string.isRequired,
};

export default PhotoGallery;
